import {
  Box, Card, Flex, LinkBox, LinkOverlay, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { Text10, Text12 } from "components/Text";
import { titleize, truncateEnd } from "helpers/string";
import { convertToTimezoneUSATimeString } from "helpers/time";
import { CalendarEvent } from "../types";

interface EventRowProps {
  event: CalendarEvent;
  timezone: string;
  handleClick: (event: CalendarEvent) => void;
}

function EventRow({ event, timezone, handleClick }: EventRowProps) {
  const formattedStartTime = convertToTimezoneUSATimeString(event.start, timezone);
  const headerTextColor = useColorModeValue("cdsGrey.900", "var(--chakra-colors-whiteAlpha-900)");
  const formattedEndTime = convertToTimezoneUSATimeString(event.end, timezone);
  const showEndTime = event?.showEnd ?? true;
  return (
    <LinkBox
      as={Card}
      height="auto"
      backgroundColor={event.style?.bg}
      opacity={event.style?.opacity}
      border={event.style?.border}
    >
      <Flex py="1" px="1" direction="column" h="100%">
        <Box w="100%">
          <Text12 fontWeight="700" color={headerTextColor} textAlign="left" noOfLines={1}>
            <LinkOverlay href="#" onClick={() => handleClick(event)}>
              {truncateEnd(event.title, 38)}
            </LinkOverlay>
          </Text12>
        </Box>
        <Text10>{formattedEndTime && showEndTime ? `${formattedStartTime} – ${formattedEndTime}` : formattedStartTime}</Text10>
      </Flex>
    </LinkBox>
  );
}

type EventsCardProps = {
  eventsByType: CalendarEvent[];
  handleClick: (event: CalendarEvent) => void;
  timezone: string;
};

export default function EventsCard({
  eventsByType,
  timezone,
  handleClick,
}: EventsCardProps) {
  if (!eventsByType || (eventsByType || []).length === 0) {
    return null;
  }

  const { style, eventType } = eventsByType[0];
  const numberOfEvents = eventsByType.length;
  const eventTitle = `${numberOfEvents} ${eventType ? titleize(eventType) : "Event"}${numberOfEvents > 1 ? "s" : ""}`;

  return (
    <Popover trigger="hover" isLazy>
      <PopoverTrigger>
        <Box
          bg={style?.bg || "blue.100"}
          borderRadius="md"
          px="2"
          py="1"
          cursor="pointer"
          _hover={{ opacity: 0.8 }}
          w="full"
        >
          <Text
            fontSize="xs"
            fontWeight="medium"
            color="white"
            noOfLines={1}
          >
            {eventTitle}
          </Text>
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody>
            <Stack gap="1">
              {eventsByType.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  handleClick={handleClick}
                  timezone={timezone}
                />
              ))}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
